import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './HeroHeader.css';
import bgMusic from '../assets/bksong.mp3';

const HeroHeader = () => {
    const [isPlaying, setIsPlaying] = useState(true); // Default to true to show playing icon
    const audioRef = useRef(null);
    const heroWrapperRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: heroWrapperRef,
        offset: ["start start", "end start"]
    });

    const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
    const contentScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
    const swipeIndicatorOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

    useEffect(() => {
        audioRef.current = new Audio(bgMusic);
        audioRef.current.loop = true;

        // Force play on load
        const playAudio = () => {
            if (audioRef.current) {
                audioRef.current.play().then(() => {
                    setIsPlaying(true);
                }).catch((error) => {
                    console.log("Autoplay blocked. Retrying...", error);
                    setIsPlaying(false);
                });
            }
        };

        // Try playing immediately
        playAudio();

        // Also try playing after a short delay (sometimes helps with browser loading)
        const timeoutId = setTimeout(() => {
            playAudio();
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Also attach a window-level event to catch any interaction to start music
    useEffect(() => {
        const forcePlayOnInteract = () => {
            if (audioRef.current && audioRef.current.paused) {
                audioRef.current.play().then(() => {
                    setIsPlaying(true);
                }).catch(e => console.log("Force play failed:", e));
            }
        };

        window.addEventListener('click', forcePlayOnInteract);
        window.addEventListener('touchstart', forcePlayOnInteract);
        window.addEventListener('scroll', forcePlayOnInteract);

        return () => {
            window.removeEventListener('click', forcePlayOnInteract);
            window.removeEventListener('touchstart', forcePlayOnInteract);
            window.removeEventListener('scroll', forcePlayOnInteract);
        };
    }, []);


    const togglePlay = (e) => {
        if (e) e.stopPropagation();
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            window.scrollTo({
                top: element.offsetTop,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div ref={heroWrapperRef} className="hero-wrapper">
            <motion.div
                className="hero-background"
                style={{ y: backgroundY }}
            />
            {/* Top Navigation Links */}
            <motion.nav
                className="top-nav"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
            >
                <span onClick={() => scrollToSection('details')}>Details</span>
                <span onClick={() => scrollToSection('rsvp')}>RSVP</span>
                <span onClick={() => scrollToSection('gallery')}>Gallery</span>
            </motion.nav>

            <motion.div
                className="hero-content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                style={{
                    opacity: contentOpacity,
                    scale: contentScale
                }}
            >
                <div className="status-pill">
                    WE'RE GETTING MARRIED
                </div>

                <h1 className="couple-names">Sandeepani & Ruwanara</h1>

                <p className="wedding-date"> May 28, 2026 / 30 May 2026</p>
            </motion.div>

            <motion.div
                className="swipe-indicator"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                style={{ opacity: swipeIndicatorOpacity }}
                onClick={() => scrollToSection('quote-section')} // Added onClick handler
            >
                <span>Swipe up for more</span>
                <span className="chevron">⌄</span>
            </motion.div>

            {/* Floating Music Toggle */}
            <motion.div
                className="music-toggle"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 2.5, type: "spring" }}
                onClick={togglePlay}
            >
                <span className="music-icon">{isPlaying ? '🔊' : '🔇'}</span>
            </motion.div>
        </div>
    );
};

export default HeroHeader;
